import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTabs from "../../components/DstTabs/DstTabs";
import InfoRow from "../../components/InfoRow/InfoRow";
import Lister from "../../components/Lister/Lister";
import { useSession } from "../../contexts/SessionContext";
import { Ephemeral } from "../../models/Ephemeral";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { fetchEphemeralSession } from "./EphemeralSession.function";
import SessionExercises from "./components/SessionExercises/SessionExercises";
import SessionInvitationModal from "./components/SessionInvitationModal/SessionInvitationModal";

const EphemeralSession = () => {
  const { session_id } = useParams();
  const { language } = useSession();
  const [ephemeralSession, setEphemeralSession] = useState<Ephemeral | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("information");
  const [showNotes, setShowNotes] = useState(true);
  const [showInvitationModal, setShowInvitationModal] = useState(false);

  useEffect(() => {
    fetchEphemeralSession(session_id, setEphemeralSession, setLoaded, language);
  }, [session_id, language]);

  const fields = useMemo(
    () =>
      ephemeralSession
        ? [
            {
              icon: "dst-icon-tag-01",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.TITLE_FIELD"),
              value: ephemeralSession.title,
            },
            {
              icon: "dst-icon-file-02",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.CURSUS"),
              value: ephemeralSession.cursus || "—",
            },
            {
              icon: "dst-icon-clock",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.DURATION"),
              value: ephemeralSession.duration,
            },
            {
              icon: "dst-icon-translate-01",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.LANGUAGE"),
              value:
                ephemeralSession.language === "fr"
                  ? translate(language, "PAGES.EPHEMERAL_SESSION.LANGUAGES.FRENCH")
                  : ephemeralSession.language === "en"
                  ? translate(language, "PAGES.EPHEMERAL_SESSION.LANGUAGES.ENGLISH")
                  : ephemeralSession.language || "—",
            },
            {
              icon: "dst-icon-divide-03",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.MANDATORY_RATIO"),
              value: ephemeralSession.mandatory_ratio,
            },
            {
              icon: "dst-icon-hash-01",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.POOL_LENGTH"),
              value: ephemeralSession.pool_length ?? "—",
            },
            {
              icon: "dst-icon-check",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.RANDOMIZE"),
              value: translate(
                language,
                ephemeralSession.randomize ? "PAGES.EPHEMERAL_SESSION.YES" : "PAGES.EPHEMERAL_SESSION.NO"
              ),
            },
            {
              icon: "dst-icon-mail-01",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.SEND_RESULT"),
              value: translate(
                language,
                ephemeralSession.send_result ? "PAGES.EPHEMERAL_SESSION.YES" : "PAGES.EPHEMERAL_SESSION.NO"
              ),
            },
            {
              icon: "dst-icon-calendar-date",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.CREATED_DATE"),
              value: formatDateHumanly(language, new Date(ephemeralSession.dt_created * 1000).toISOString(), true),
            },
            {
              icon: "dst-icon-calendar-date",
              label: translate(language, "PAGES.EPHEMERAL_SESSION.UPDATED_DATE"),
              value: ephemeralSession.dt_updated
                ? formatDateHumanly(language, new Date(ephemeralSession.dt_updated * 1000).toISOString(), true)
                : "—",
            },
          ]
        : [],
    [ephemeralSession, language]
  );

  return (
    <div className="p-3 p-md-4">
      <Helmet>
        <title>
          QCM session details
          {ephemeralSession ? ` - ${ephemeralSession.name}` : ""}
        </title>
      </Helmet>
      <div className="d-flex align-items-center flex-wrap gap-3 mb-4">
        <Link
          to="/ephemeral/sessions"
          className="p-2 medium-gray-background rounded clickable text-body text-decoration-none"
          aria-label={translate(language, "PAGES.EPHEMERAL_SESSION.BACK")}
          title={translate(language, "PAGES.EPHEMERAL_SESSION.BACK")}
        >
          <span className="dst-icon-chevron-left" aria-hidden="true" />
        </Link>
        <h2 className="mb-0 fw-bold">{translate(language, "PAGES.EPHEMERAL_SESSION.TITLE")}</h2>
        {ephemeralSession ? (
          <div className="ms-auto">
            <PermissionComponent
              neededPermission={[{ methods: ["POST"], route: "/ephemeral-evaluations/sessions/*/attempts" }]}
            >
              <DstButton
                btnImageBefore={<span className="dst-icon-user-plus-02 me-2" />}
                clickFunction={() => setShowInvitationModal(true)}
                value={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.BUTTONS.INVITE")}
              />
            </PermissionComponent>
          </div>
        ) : null}
      </div>
      {!loaded ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-muted text-center py-5" role="status">
            {translate(language, "PAGES.EPHEMERAL_SESSION.LOADING")}
          </Card.Body>
        </Card>
      ) : !ephemeralSession ? null : (
        <React.Fragment>
          <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <code className="text-break">{ephemeralSession.ID}</code>
              <h3 className="fw-semibold mb-0">{ephemeralSession.name}</h3>
            </div>
            <Badge bg={ephemeralSession.is_deprecated ? "secondary" : "success"} className="rounded-pill px-3 py-2">
              {translate(
                language,
                ephemeralSession.is_deprecated ? "PAGES.EPHEMERAL_SESSION.DEPRECATED" : "PAGES.EPHEMERAL_SESSION.ACTIVE"
              )}
            </Badge>
          </div>
          <SessionInvitationModal
            onClose={() => setShowInvitationModal(false)}
            sessionId={String(ephemeralSession.ID)}
            show={showInvitationModal}
          />
          <DstTabs
            variant="primary"
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key || "information")}
            tabsData={[
              {
                id: "information",
                title: translate(language, "PAGES.EPHEMERAL_SESSION.INFORMATION"),
                content: (
                  <React.Fragment>
                    <div className="mb-4">
                      <Row className="gx-5">
                        {fields.map(({ icon, label, value }) => (
                          <Col xs={12} md={6} key={label}>
                            {InfoRow(icon, label, <span className="text-break">{value}</span>)}
                          </Col>
                        ))}
                      </Row>
                    </div>
                    <div className="mb-4">
                      <Row>
                        <Col>
                          <h3 className="mb-3 m-bold">{translate(language, "PAGES.EPHEMERAL_SESSION.TOPICS_RATIO")}</h3>
                          {ephemeralSession.topics_ratio?.length ? (
                            <Lister
                              items={ephemeralSession.topics_ratio}
                              columns={[
                                {
                                  header: translate(language, "PAGES.EPHEMERAL_SESSION.TOPIC_NAME"),
                                  key: "name",
                                },
                                {
                                  header: translate(language, "PAGES.EPHEMERAL_SESSION.TOPIC_PERCENTAGE"),
                                  key: "percentage",
                                },
                              ]}
                            />
                          ) : (
                            <p className="text-muted mb-0">
                              {translate(language, "PAGES.EPHEMERAL_SESSION.NO_TOPICS")}
                            </p>
                          )}
                        </Col>
                        <Col>
                          <div className="d-flex align-items-center justify-content-between my-3">
                            <h3 className="mb-0 m-bold">{translate(language, "PAGES.EPHEMERAL_SESSION.NOTES")}</h3>
                            <span
                              className={`clickable dst-icon-chevron-${showNotes ? "up" : "down"}`}
                              onClick={() => setShowNotes(!showNotes)}
                            />
                          </div>
                          {showNotes && (
                            <p className="mb-0 medium-gray-background p-3 rounded w-100 s-regular text-pre-wrap text-break">
                              {ephemeralSession.notes || "—"}
                            </p>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </React.Fragment>
                ),
              },
              {
                id: "exercises",
                title: `${translate(language, "PAGES.EPHEMERAL_SESSION.EXOS")} (${ephemeralSession.exos.length})`,
                content: <SessionExercises exerciseIds={ephemeralSession.exos} sessionId={ephemeralSession.ID} />,
              },
            ]}
          />
        </React.Fragment>
      )}
    </div>
  );
};

export default EphemeralSession;
